// ============================================================================
// Photo assignment AI output runtime contract (TS-012E)
// ----------------------------------------------------------------------------
// Validates the photo-auto-assign envelope (per-photo dispositions + hero
// suggestion) before existing normalisers consume it.
//
// The file kept its name when the AI markup contracts it also held (the
// markup regions and the old Analyze Photo defects) were removed with
// the AI photo markup on 2026-10-13.
//
// Phase 1 posture (DEC-COUNCIL-010): permissive and behaviour-preserving.
// Reject only top-level shapes the downstream code cannot inspect at all.
// Malformed nested values warn so strict disposition coverage and hero safety
// gates remain authoritative.
// ============================================================================

import { z } from 'zod';

const AnyObjectSchema = z.object({}).passthrough();

export const PhotoAutoAssignOutputSchema = z
    .object({
        dispositions: z.unknown().optional(),
        heroPhotoIndex: z.unknown().optional(),
        reasoning: z.unknown().optional(),
    })
    .passthrough();

export type PhotoAutoAssignOutput = z.infer<typeof PhotoAutoAssignOutputSchema>;

export type PhotoAiValidationFailureReason =
    | 'null'
    | 'array'
    | 'not_object'
    | 'schema_failed';

export interface PhotoAiValidationFailure {
    ok: false;
    reason: PhotoAiValidationFailureReason;
    issues: string[];
}

export interface PhotoAutoAssignValidationOk {
    ok: true;
    value: PhotoAutoAssignOutput;
    warnings: string[];
}

export type PhotoAutoAssignValidationResult =
    | PhotoAutoAssignValidationOk
    | PhotoAiValidationFailure;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasOwn(obj: Record<string, unknown>, key: string): boolean {
    return Object.prototype.hasOwnProperty.call(obj, key);
}

function zodIssues(error: z.ZodError): string[] {
    return error.issues.map((issue) => {
        const path = issue.path.length > 0 ? issue.path.join('.') : '<root>';
        return `${path}: ${issue.message}`;
    });
}

function rejectNonObject(value: unknown): PhotoAiValidationFailure | null {
    if (value === null) return { ok: false, reason: 'null', issues: ['value is null'] };
    if (Array.isArray(value)) {
        return {
            ok: false,
            reason: 'array',
            issues: ['top-level value is an array, expected object'],
        };
    }
    if (typeof value !== 'object') {
        return {
            ok: false,
            reason: 'not_object',
            issues: [`top-level type is ${typeof value}, expected object`],
        };
    }
    return null;
}

function looksLikeIntegerIndex(value: unknown): boolean {
    return value !== null && value !== '' && Number.isInteger(Number(value));
}

function validateDispositionWarnings(dispositions: unknown): string[] {
    const warnings: string[] = [];
    if (!Array.isArray(dispositions)) return warnings;

    let nonObjectDispositions = 0;
    let malformedDispositions = 0;

    for (const disposition of dispositions) {
        if (!isRecord(disposition)) {
            nonObjectDispositions += 1;
            continue;
        }
        const assigned = disposition.outcome === 'assigned';
        const omitted = disposition.outcome === 'omitted';
        if (!looksLikeIntegerIndex(disposition.photoIndex)
            || (!assigned && !omitted)
            || (assigned && !looksLikeIntegerIndex(disposition.scopeIndex))
            || (omitted && disposition.scopeIndex !== null)
            || typeof disposition.reason !== 'string') {
            malformedDispositions += 1;
        }
    }

    if (nonObjectDispositions > 0) {
        warnings.push(`${nonObjectDispositions} disposition entries are non-objects; strict photo assignment validation will reject them.`);
    }
    if (malformedDispositions > 0) {
        warnings.push(`${malformedDispositions} disposition entries have invalid index/outcome/scope/reason fields; strict photo assignment validation will reject them.`);
    }

    return warnings;
}

export function validatePhotoAutoAssignOutput(
    value: unknown
): PhotoAutoAssignValidationResult {
    const topLevelFailure = rejectNonObject(value);
    if (topLevelFailure) return topLevelFailure;

    const result = PhotoAutoAssignOutputSchema.safeParse(value);
    if (!result.success) {
        return { ok: false, reason: 'schema_failed', issues: zodIssues(result.error) };
    }

    const warnings: string[] = [];
    const parsed = result.data;
    if (!hasOwn(parsed, 'dispositions')) {
        warnings.push('photo-auto-assign output has no dispositions field; strict service validation will reject it.');
    } else if (parsed.dispositions == null) {
        warnings.push('photo-auto-assign output has null dispositions; strict service validation will reject it.');
    } else if (!Array.isArray(parsed.dispositions)) {
        warnings.push('photo-auto-assign output has non-array dispositions; strict service validation will reject it.');
    } else {
        warnings.push(...validateDispositionWarnings(parsed.dispositions));
    }

    if (hasOwn(parsed, 'heroPhotoIndex') && parsed.heroPhotoIndex != null && !looksLikeIntegerIndex(parsed.heroPhotoIndex)) {
        warnings.push('photo-auto-assign output has non-integer heroPhotoIndex; existing hero safety gate will ignore it.');
    }
    if (hasOwn(parsed, 'reasoning') && parsed.reasoning != null && typeof parsed.reasoning !== 'string') {
        warnings.push('photo-auto-assign output has non-string reasoning; existing flow will use an empty string.');
    }

    // Ensure the generic schema stays permissive if a future Zod option would
    // reject unexpected object wrappers.
    const objectCheck = AnyObjectSchema.safeParse(parsed);
    if (!objectCheck.success) {
        return { ok: false, reason: 'schema_failed', issues: zodIssues(objectCheck.error) };
    }

    return { ok: true, value: parsed, warnings };
}
